import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';

/**
 * A page title, one pattern for every screen.
 *
 * Eyebrow, title, subtitle, actions. The eyebrow says where a person is - the
 * object type, the workspace, the module - and the subtitle names the scope of
 * any figure the page shows, which is the line that stops a total from being
 * read as covering more than it does.
 *
 * Destructive actions do not belong in this row. A control that ends a thing
 * sitting a few pixels from the one that saves it is a control somebody will
 * eventually hit by muscle memory; those go in the overflow beside the record
 * they would destroy.
 */
export function PageHeading({
  kicker,
  children,
  lede,
  actions,
  className,
  ...rest
}: {
  /** Where this is: the object type, the module, the workspace. */
  kicker?: ReactNode;
  children: ReactNode;
  /** What the page covers, and what any figure on it leaves out. */
  lede?: ReactNode;
  /** Page-level controls, primary last. Never a destructive one. */
  actions?: ReactNode;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <header
      {...rest}
      data-component="PageHeading"
      className={cx('flex flex-wrap items-start gap-5', className)}
    >
      <div className="min-w-0 flex-1">
        {kicker === undefined ? null : (
          <p className="pb-1.5 text-micro uppercase tracking-kicker text-bronze-text">{kicker}</p>
        )}
        <h1 className="serif-moment text-h1 leading-heading text-ink">{children}</h1>
        {lede === undefined ? null : (
          <p className="max-w-reading pt-1 text-body text-ink-meta">{lede}</p>
        )}
      </div>
      {actions === undefined ? null : (
        <div className="flex items-center gap-2 pt-1 ms-auto">{actions}</div>
      )}
    </header>
  );
}
